import React, { useMemo, useRef, useState, useCallback } from 'react';
import { ReactFlow, ReactFlowProvider, Background, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import 'features/agents/WorkflowEditor/editor.css';

import AgentNode from 'features/agents/canvas/AgentNode';
import FlowEdge from 'features/agents/canvas/FlowEdge';
import CanvasControls, { AddStepButton } from 'features/agents/canvas/CanvasControls';
import useFitOnChange, { FIT_OPTIONS, fitGraph } from 'features/agents/canvas/useFitOnChange';
import { CanvasActionsContext } from 'features/agents/canvas/CanvasActionsContext';
import { NODE_CATALOG, portsOf, portLabel } from 'features/agents/WorkflowEditor/nodeCatalog';
import { summaryFor } from 'features/agents/WorkflowEditor/nodeAppearance';
import { layoutGraph, measureNode, labelledPorts } from 'features/agents/WorkflowEditor/layoutGraph';
import { isTriggerKind } from 'features/agents/WorkflowEditor/definition';

const NODE_TYPES = { agent: AgentNode };
const EDGE_TYPES = { flow: FlowEdge };
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.25;
const PAN_PAD = 640;
const MINIMAP_FROM = 8;
const NO_ACTIONS = {};

function translateExtentOf(nodes) {
  if (!nodes.length) return undefined;
  let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
  for (const n of nodes) {
    x1 = Math.min(x1, n.position.x); y1 = Math.min(y1, n.position.y);
    x2 = Math.max(x2, n.position.x + n.data.size.w); y2 = Math.max(y2, n.position.y + n.data.size.h);
  }
  return [[x1 - PAN_PAD, y1 - PAN_PAD], [x2 + PAN_PAD, y2 + PAN_PAD]];
}

const edgeKey = (e) => `${e.from}>${e.to}>${e.port ?? ''}`;

/**
 * The agent graph: laid out from the definition, never dragged, never stored.
 * One canvas for the editor (mode "edit") and the run view (mode "run"), so
 * a step looks the same wherever it appears.
 *
 * @param {object} props
 * @param {{nodes: Array, edges: Array}} props.definition
 * @param {Object<string, {status?: string, duration_ms?: number}>} [props.stepByNodeId]
 *   run/test results by node id. Any entries switch statuses on.
 * @param {'edit'|'run'} [props.mode]   run: no editing affordances; a node
 *   with no step shows as skipped (unless skipUnrun is false)
 * @param {string|null} [props.selectedId]
 * @param {(event, node) => void} [props.onNodeClick]
 * @param {boolean} [props.readOnly]    no connecting, no "+", no deleting
 * --- editor only ---
 * @param {object} [props.actions]      CanvasActionsContext value
 * @param {Object<string,string>} [props.problemByNode]  node id -> message
 * @param {(conn) => void} [props.onConnect]
 * @param {(edges) => void} [props.onEdgesDelete]
 * @param {boolean} [props.panelOpen]   a panel is taking space beside the canvas
 * @param {() => void} [props.onAddStep] shows the "Add step" button
 * @param {boolean} [props.skipUnrun]
 */
export default function FlowCanvas(props) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}

function CanvasInner({
  definition, stepByNodeId, mode = 'edit', selectedId = null, onNodeClick, readOnly = false,
  actions, problemByNode, onConnect, onEdgesDelete, panelOpen = false, onAddStep, skipUnrun,
}) {
  const rf = useReactFlow();
  const wrapRef = useRef(null);
  const [connecting, setConnecting] = useState(false);
  const runView = mode === 'run';
  const locked = readOnly || runView;
  const steps = stepByNodeId || {};
  const showStatus = runView || Object.keys(steps).length > 0;
  const skipMissing = skipUnrun ?? runView;
  const nodesIn = definition?.nodes || [];
  const edgesIn = definition?.edges || [];

  const positions = useMemo(() => layoutGraph({ nodes: nodesIn, edges: edgesIn }, (k) => NODE_CATALOG[k]), [nodesIn, edgesIn]);

  const rfNodes = useMemo(() => {
    const hasOut = new Map();
    for (const e of edgesIn) hasOut.set(`${e.from}|${e.port ?? ''}`, true);
    return nodesIn.map((n) => {
      const ports = portsOf(n);
      const outs = ports || [null];
      const labelled = !!labelledPorts(n);
      const step = showStatus ? steps[n.id] : null;
      const status = step?.status || (showStatus && skipMissing ? 'skipped' : null);
      return {
        id: n.id,
        type: 'agent',
        position: positions.get(n.id) || { x: 40, y: 40 },
        draggable: false,
        selected: n.id === selectedId,
        sourcePosition: 'bottom',
        targetPosition: 'top',
        data: {
          kind: n.kind,
          name: n.name,
          summary: summaryFor(n.kind, n.config),
          mode,
          readOnly: locked,
          invalid: !!problemByNode?.[n.id],
          invalidMsg: problemByNode?.[n.id] || null,
          status,
          durationMs: step?.duration_ms ?? null,
          openPorts: outs.filter((p) => !hasOut.get(`${n.id}|${p ?? ''}`)),
          // Every port with what people call it; `labelled` prints them on the node.
          ports: ports ? ports.map((p) => ({ id: p, label: portLabel(n, p) })) : null,
          labelled,
          size: measureNode(n),
          showRun: !showStatus,
        },
      };
    });
  }, [nodesIn, edgesIn, positions, selectedId, mode, locked, steps, showStatus, skipMissing, problemByNode]);

  const nodeById = useMemo(() => new Map(nodesIn.map((n) => [n.id, n])), [nodesIn]);
  const rfEdges = useMemo(() => edgesIn.map((e) => {
    const status = showStatus ? steps[e.to]?.status || null : null;
    const from = nodeById.get(e.from);
    // A node that prints its ports already names the branch; the edge stays bare.
    const pill = e.port && !labelledPorts(from) ? portLabel(from, e.port) : null;
    return {
      id: edgeKey(e),
      source: e.from,
      target: e.to,
      sourceHandle: e.port || undefined,
      type: 'flow',
      selectable: !locked,
      focusable: false,
      data: { port: e.port || null, pill, from: e.from, to: e.to, canEdit: !locked, runMode: showStatus, status },
    };
  }), [edgesIn, nodeById, steps, showStatus, locked]);

  const kindById = useMemo(() => new Map(nodesIn.map((n) => [n.id, n.kind])), [nodesIn]);
  const isValidConnection = useCallback((c) => {
    if (!c.source || !c.target || c.source === c.target) return false;
    if (isTriggerKind(kindById.get(c.target))) return false;
    const port = c.sourceHandle || null;
    return !edgesIn.some((e) => e.from === c.source && e.to === c.target && (e.port ?? null) === port);
  }, [kindById, edgesIn]);

  const fitKey = `${nodesIn.length}:${edgesIn.length}:${mode}`;
  const { markUserMoved } = useFitOnChange(rf, wrapRef, fitKey);
  const onFit = useCallback(() => fitGraph(rf, wrapRef.current), [rf]);
  const translateExtent = useMemo(() => translateExtentOf(rfNodes), [rfNodes]);

  // Backspace deletes the selected EDGE, and a settings panel is full of
  // text fields: with the panel open the key does nothing, so a stray
  // Backspace can never remove a connection (a step has its own trash button).
  const deleteKey = locked || panelOpen ? null : ['Delete', 'Backspace'];

  return (
    <CanvasActionsContext.Provider value={actions || NO_ACTIONS}>
      <div ref={wrapRef} className={`agent-flow${locked ? ' is-readonly' : ''}${connecting ? ' is-connecting' : ''}`} style={{ position: 'absolute', inset: 0 }}>
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={NODE_TYPES}
          edgeTypes={EDGE_TYPES}
          defaultEdgeOptions={{ type: 'flow' }}
          onConnect={locked ? undefined : onConnect}
          onEdgesDelete={locked ? undefined : onEdgesDelete}
          onConnectStart={() => setConnecting(true)}
          onConnectEnd={() => setConnecting(false)}
          isValidConnection={isValidConnection}
          onNodeClick={onNodeClick}
          onMoveStart={(event) => { if (event) markUserMoved(); }}
          nodesDraggable={false}
          nodesConnectable={!locked}
          nodesFocusable={false}
          edgesFocusable={false}
          elementsSelectable={!locked}
          deleteKeyCode={deleteKey}
          selectNodesOnDrag={false}
          multiSelectionKeyCode={null}
          proOptions={{ hideAttribution: true }}
          fitView
          fitViewOptions={FIT_OPTIONS}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          translateExtent={translateExtent}
          panOnDrag
          panOnScroll
          panOnScrollSpeed={1.1}
          zoomOnScroll
          zoomActivationKeyCode={['Control', 'Meta']}
          zoomOnPinch
          zoomOnDoubleClick={false}
          preventScrolling
        >
          <Background variant="dots" gap={20} size={1} />
          <CanvasControls showMiniMap={nodesIn.length >= MINIMAP_FROM && !panelOpen} onFit={onFit} />
        </ReactFlow>
        {!locked && onAddStep && <AddStepButton onClick={onAddStep} />}
      </div>
    </CanvasActionsContext.Provider>
  );
}
